import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import type { ImageSourcePropType, StyleProp, ViewStyle } from 'react-native';
import { images } from 'app/design/images/index';
import { PCImage } from 'app/design/primitives/image';
import { ImageBackground } from 'expo-image';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCirclePlus } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isAnyWhiteLabel, isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { useUser } from 'app/network/user';
import { useTrackNewListingSource } from 'app/network/listing';
import { autoplayCarouselFrom } from 'app/screens/offer-hub/carousel-animation';
import { CAROUSEL_PERSPECTIVE, carouselPose } from 'app/screens/offer-hub/carousel-geometry';
import Animated, {
  cancelAnimation,
  useAnimatedStyle,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
const theme = createDSTheme();
const isRefrain = isWhiteLabel(WhiteLabel.Refrain);

interface MultipleOfferCardProps {
  animateDesktopPhotos?: boolean;
  desktopImageSources?: [ImageSourcePropType, ImageSourcePropType, ImageSourcePropType];
  onPublish?: () => void;
  style?: StyleProp<ViewStyle>;
}

const CARD_WIDTH = 120;
const CARD_HEIGHT = 130;
const desktopScaleFactor = 1.3;
const CAROUSEL_CARD_COUNT = 3;

function getPromoLayout(viewportWidth: number) {
  const isLargeUI = viewportWidth >= 856;
  const isDesktopCompact = isLargeUI && viewportWidth < 1360;
  const isDesktopTight = isLargeUI && viewportWidth < 1180;
  const desktopCardsScale = isDesktopTight ? 1.22 : isDesktopCompact ? 1.38 : desktopScaleFactor + 0.26;
  const desktopCardHeight = isDesktopTight ? 390 : isDesktopCompact ? 425 : 460;
  return {
    contentDesktopDynamicStyle: {
      minHeight: desktopCardHeight,
      maxWidth: isDesktopTight ? 520 : isDesktopCompact ? 600 : 700,
      paddingVertical: isDesktopTight
        ? theme.spacing['spacing.general.x8']
        : isDesktopCompact
          ? theme.spacing['spacing.general.x10']
          : 56,
      paddingRight: isDesktopTight
        ? theme.spacing['spacing.general.x4']
        : isDesktopCompact
          ? theme.spacing['spacing.general.x5']
          : theme.spacing['spacing.general.x12'],
    },
    desktopCardsHeight: CARD_HEIGHT * desktopCardsScale,
    desktopCardsInsetRight: isDesktopTight ? 38 : isDesktopCompact ? 52 : 82,
    desktopCardsSpread: Math.round(72 * desktopCardsScale),
    desktopCardsWidth: CARD_WIDTH * desktopCardsScale,
    desktopContainerDynamicStyle: { minHeight: desktopCardHeight },
    hideCards: viewportWidth < 856,
    isLargeUI,
    titleDesktopStyle: {
      fontSize: isDesktopTight ? 44 : isDesktopCompact ? 48 : 54,
      lineHeight: isDesktopTight ? 50 : isDesktopCompact ? 54 : 60,
      maxWidth: isDesktopTight ? 500 : isDesktopCompact ? 560 : 620,
    },
  };
}

function MobilePromoCards({ isHauteGaspesie, visible }: { isHauteGaspesie: boolean; visible: boolean }) {
  if (!visible) return null;
  return (
    <View style={styles.fansContainer}>
      <View
        style={[
          styles.itemCard,
          styles.itemCardLeft,
          {
            backgroundColor: isAnyWhiteLabel()
              ? theme.colors['surface.secondary-alt']
              : theme.colors['surface.brand.secondary-4.default'],
          },
        ]}
      >
        <PCImage
          contentFit="contain"
          source={images.thumbnail2}
          style={[
            StyleSheet.absoluteFill,
            { borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 4), transform: 'scale(0.9)' },
          ]}
        />
      </View>
      <View
        style={[
          styles.itemCard,
          styles.itemCardCenter,
          {
            backgroundColor: isRefrain
              ? theme.colors['surface.secondary']
              : isAnyWhiteLabel()
                ? theme.colors['surface.brand.strong-alt']
                : theme.colors['surface.brand.secondary-1.default'],
          },
        ]}
      >
        <PCImage
          aspectRatio={isHauteGaspesie ? undefined : 1.3}
          contentFit="contain"
          source={images.thumbnail1}
          style={[
            StyleSheet.absoluteFill,
            { borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 4) },
            isHauteGaspesie && styles.hauteGaspesieCenterImage,
          ]}
        />
      </View>
      <View
        style={[
          styles.itemCard,
          styles.itemCardRight,
          {
            backgroundColor: isRefrain
              ? theme.colors['surface.brand.default-alt']
              : isAnyWhiteLabel()
                ? theme.colors['surface.secondary']
                : theme.colors['surface.brand.secondary-2.default'],
          },
        ]}
      >
        <PCImage
          contentFit="contain"
          source={images.thumbnail3}
          style={[
            StyleSheet.absoluteFill,
            { borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 4), transform: 'scale(0.9)' },
          ]}
        />
      </View>
    </View>
  );
}

function DesktopPromoCards({
  height,
  insetRight,
  spread,
  visible,
  width,
}: {
  height: number;
  insetRight: number;
  spread: number;
  visible: boolean;
  width: number;
}) {
  if (!visible) return null;
  const cards = [
    {
      image: images.thumbnail2,
      backgroundColor: isRefrain ? 'surface.secondary-alt' : 'surface.brand.secondary-4.default',
      rotate: '-18deg',
      x: -spread,
    },
    {
      image: images.thumbnail1,
      backgroundColor: isRefrain ? 'surface.secondary' : 'surface.brand.secondary-1.default',
      rotate: '-4deg',
      x: 0,
    },
    {
      image: images.thumbnail3,
      backgroundColor: isRefrain ? 'surface.brand.default-alt' : 'surface.brand.secondary-2.default',
      rotate: '14deg',
      x: spread,
    },
  ] as const;
  return (
    <View pointerEvents="none" style={[styles.desktopCardsLayer, { paddingRight: insetRight }]}>
      <View style={[styles.desktopCardsCluster, { width: width + spread * 2, height: height + 24 }]}>
        {cards.map((card, index) => (
          <View
            key={card.rotate}
            style={[
              styles.itemCard,
              {
                backgroundColor: theme.colors[card.backgroundColor],
                height,
                width,
                transform: [{ rotate: card.rotate }, ...(card.x ? [{ translateX: card.x }] : [])],
                zIndex: index === 1 ? 3 : index === 0 ? 1 : 2,
              },
            ]}
          >
            <PCImage
              contentFit="contain"
              source={card.image}
              style={[
                StyleSheet.absoluteFill,
                { borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 4) },
                index === 1 ? { transform: 'scale(1.1)' } : undefined,
              ]}
            />
          </View>
        ))}
      </View>
    </View>
  );
}

function useDesktopPhotoStyles(
  progress: SharedValue<number>,
  reveal: SharedValue<number>,
  index: number,
  spread: number,
) {
  const pose = useDerivedValue(() => carouselPose(index, progress.value, CAROUSEL_CARD_COUNT, spread));
  const layerStyle = useAnimatedStyle(() => ({ zIndex: pose.value.layer }));
  const cardStyle = useAnimatedStyle(() => {
    const { depth, rotateY, scale, translateX, translateY } = pose.value;
    return {
      shadowOpacity: 0.1 + depth * 0.18,
      shadowRadius: 6 + depth * 6,
      transform: [
        { perspective: CAROUSEL_PERSPECTIVE },
        { translateX },
        { translateY: translateY + (1 - reveal.value) * 36 },
        { rotateY: `${String(rotateY)}deg` },
        { scale },
      ],
    };
  });
  const lightingStyle = useAnimatedStyle(() => ({ opacity: (1 - pose.value.depth) * 0.16 }));

  return { cardStyle, layerStyle, lightingStyle };
}

function isRemoteImage(source: ImageSourcePropType) {
  return typeof source === 'object' && !Array.isArray(source) && 'uri' in source && Boolean(source.uri);
}

interface AnimatedDesktopPhotoProps {
  backgroundColor: string;
  height: number;
  index: number;
  layerPosition: ViewStyle;
  progress: SharedValue<number>;
  reveal: SharedValue<number>;
  source: ImageSourcePropType;
  spread: number;
  width: number;
}

function AnimatedDesktopPhoto({
  backgroundColor,
  height,
  index,
  layerPosition,
  progress,
  reveal,
  source,
  spread,
  width,
}: AnimatedDesktopPhotoProps) {
  const photoStyles = useDesktopPhotoStyles(progress, reveal, index, spread);

  return (
    <Animated.View collapsable={false} style={[styles.animatedPhotoLayer, layerPosition, photoStyles.layerStyle]}>
      <Animated.View
        renderToHardwareTextureAndroid
        shouldRasterizeIOS
        style={[styles.animatedPhotoCard, { backgroundColor, height, width }, photoStyles.cardStyle]}
      >
        <View style={styles.animatedPhotoClip}>
          <PCImage
            contentFit={isRemoteImage(source) ? 'cover' : 'contain'}
            source={source}
            style={StyleSheet.absoluteFill}
          />
          <Animated.View
            pointerEvents="none"
            style={[StyleSheet.absoluteFill, styles.animatedPhotoLighting, photoStyles.lightingStyle]}
          />
        </View>
      </Animated.View>
    </Animated.View>
  );
}

function AnimatedDesktopPhotos({
  height,
  sources,
  spread,
  width,
}: {
  height: number;
  sources: [ImageSourcePropType, ImageSourcePropType, ImageSourcePropType];
  spread: number;
  width: number;
}) {
  const reduceMotion = useReducedMotion();
  const progress = useSharedValue(0);
  const reveal = useSharedValue(reduceMotion ? 1 : 0);
  const clusterWidth = width + spread * 2;
  const clusterHeight = height + 24;
  const layerPosition = {
    height,
    left: (clusterWidth - width) / 2,
    top: (clusterHeight - height) / 2,
    width,
  };

  useEffect(() => {
    cancelAnimation(progress);
    cancelAnimation(reveal);
    progress.set(0);
    reveal.set(reduceMotion ? 1 : 0);

    if (reduceMotion) return;

    reveal.set(withTiming(1, { duration: 420 }));
    progress.set(withDelay(600, autoplayCarouselFrom(0, CAROUSEL_CARD_COUNT)));

    return () => {
      cancelAnimation(progress);
      cancelAnimation(reveal);
    };
  }, [progress, reduceMotion, reveal]);

  return (
    <View style={[styles.desktopCardsCluster, { height: clusterHeight, width: clusterWidth }]}>
      <AnimatedDesktopPhoto
        backgroundColor={theme.colors['surface.brand.secondary-4.default']}
        height={height}
        index={2}
        layerPosition={layerPosition}
        progress={progress}
        reveal={reveal}
        source={sources[2]}
        spread={spread}
        width={width}
      />
      <AnimatedDesktopPhoto
        backgroundColor={theme.colors['surface.brand.secondary-1.default']}
        height={height}
        index={0}
        layerPosition={layerPosition}
        progress={progress}
        reveal={reveal}
        source={sources[0]}
        spread={spread}
        width={width}
      />
      <AnimatedDesktopPhoto
        backgroundColor={theme.colors['surface.brand.secondary-2.default']}
        height={height}
        index={1}
        layerPosition={layerPosition}
        progress={progress}
        reveal={reveal}
        source={sources[1]}
        spread={spread}
        width={width}
      />
    </View>
  );
}

export function LegacyMultipleOfferCard({
  animateDesktopPhotos = false,
  desktopImageSources,
  onPublish,
  style,
}: MultipleOfferCardProps) {
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();
  const trackNewListingSource = useTrackNewListingSource();
  const { t } = useTranslation('landing-page');
  const { width: viewportWidth } = useWindowDimensions();
  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const layout = getPromoLayout(viewportWidth);
  const { isLargeUI } = layout;

  return (
    <View
      style={[
        styles.container,
        isLargeUI && styles.containerDesktop,
        isLargeUI && layout.desktopContainerDynamicStyle,
        style,
      ]}
    >
      <ImageBackground
        source={images.web.login}
        style={[StyleSheet.absoluteFill, { opacity: 0.7 }]}
        contentFit="cover"
      />
      <View style={[styles.overlay, isLargeUI ? styles.overlayDesktop : styles.overlayMobile]} />
      <View
        style={[styles.content, isLargeUI && styles.contentDesktop, isLargeUI && layout.contentDesktopDynamicStyle]}
      >
        <View style={[styles.badgeRow, isLargeUI && styles.badgeRowDesktop]}>
          <View style={styles.badge}>
            <Text color="text.primary" fontWeight="700" style={styles.badgeText}>
              {t('multipleOfferCard.desktopBadge')}
            </Text>
          </View>
        </View>

        <MobilePromoCards isHauteGaspesie={isHauteGaspesie} visible={!isLargeUI} />

        <Text
          color="white"
          fontWeight="700"
          style={[styles.title, isLargeUI && styles.titleDesktop, isLargeUI && layout.titleDesktopStyle]}
        >
          {t('multipleOfferCard.title')}
        </Text>

        <Text color="white" textAlign={isLargeUI ? 'left' : 'center'} variant="bodyLarge">
          <Trans
            i18nKey={isLargeUI ? 'multipleOfferCard.desktopSubtitle' : 'multipleOfferCard.subtitle'}
            components={{ strong: <Text fontWeight="700" color="white" variant="bodyLarge" /> }}
            ns="landing-page"
          />
        </Text>

        <Pressable
          onPress={() => {
            {
              track(Event.MultipleOfferCardPressed);
              onPublish?.();
              trackNewListingSource('landing_page_growth_promo');
              navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
            }
          }}
          style={[styles.button, isLargeUI && styles.buttonDesktop]}
        >
          <IconCirclePlus strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.primary']} />
          <Text fontWeight="700" variant="bodyBase">
            {t(isLargeUI ? 'multipleOfferCard.desktopButton' : 'multipleOfferCard.button')}
          </Text>
        </Pressable>
      </View>
      <PromoDesktopPhotos
        isLargeUI={isLargeUI}
        layout={layout}
        animateDesktopPhotos={animateDesktopPhotos}
        desktopImageSources={desktopImageSources}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.borderRadii['radius.lg'],
    overflow: 'hidden',
    backgroundColor: theme.colors['text.primary'],
  },
  containerDesktop: {
    minHeight: 460,
    backgroundColor: theme.colors['text.primary'],
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors['text.primary'],
  },
  overlayMobile: {
    opacity: 0.28,
  },
  overlayDesktop: {
    opacity: 0.34,
  },
  content: {
    gap: theme.spacing['spacing.general.x4'],
    padding: theme.spacing['spacing.general.x6'],
    paddingTop: theme.spacing['spacing.general.x5'],
  },
  contentDesktop: {
    zIndex: 4,
    justifyContent: 'center',
    maxWidth: 700,
    minHeight: 460,
    paddingHorizontal: theme.spacing['spacing.general.x12'],
    paddingVertical: 48,
  },
  badgeRow: {
    alignItems: 'center',
  },
  badgeRowDesktop: {
    alignItems: 'flex-start',
  },
  badge: {
    backgroundColor: '#64BAAF', // palette.aqua.default
    borderRadius: theme.borderRadii['radius.full'],
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x0,5'],
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
  },
  fansContainer: {
    alignItems: 'center',
    height: 170,
    justifyContent: 'center',
  },
  itemCard: {
    borderWidth: 4,
    borderColor: theme.colors['surface.default'],
    bottom: theme.spacing['spacing.general.x8'],
    borderRadius: theme.borderRadii['radius.md'],
    height: CARD_HEIGHT,
    position: 'absolute',
    width: CARD_WIDTH,
  },
  itemCardLeft: {
    transform: [{ rotate: '-18deg' }, { translateX: -50 }],
    zIndex: 1,
  },
  itemCardCenter: {
    transform: [{ rotate: '-4deg' }],
    zIndex: 3,
  },
  hauteGaspesieCenterImage: {
    transform: [{ scale: 1.1 }],
  },
  itemCardRight: {
    transform: [{ rotate: '14deg' }, { translateX: 50 }],
    zIndex: 2,
  },
  title: {
    textAlign: 'center',
    fontSize: 22,
  },
  titleDesktop: {
    fontSize: 54,
    lineHeight: 60,
    maxWidth: 620,
    textAlign: 'left',
  },
  subtitleDesktop: {
    fontSize: 22,
    lineHeight: 34,
    maxWidth: 620,
  },
  button: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.default'],
    borderRadius: theme.borderRadii['radius.full'],
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x1'],
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['spacing.general.x6'],
    paddingVertical: theme.spacing['spacing.general.x4'],
    width: 'auto',
  },
  buttonDesktop: {
    alignSelf: 'flex-start',
    minHeight: 58,
    paddingHorizontal: 30,
  },
  desktopCardsLayer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  desktopCardsCluster: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  animatedPhotoLayer: {
    position: 'absolute',
  },
  animatedPhotoCard: {
    borderColor: theme.colors['surface.default'],
    borderRadius: theme.borderRadii['radius.md'],
    borderWidth: 4,
    elevation: 5,
    shadowColor: theme.colors['shadow.lg'],
    shadowOffset: { width: 0, height: 3 },
  },
  animatedPhotoClip: {
    borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 4),
    flex: 1,
    overflow: 'hidden',
  },
  animatedPhotoLighting: {
    backgroundColor: '#111827',
  },
});

function PromoDesktopPhotos({
  isLargeUI,
  layout,
  animateDesktopPhotos,
  desktopImageSources,
}: {
  isLargeUI: boolean;
  layout: {
    contentDesktopDynamicStyle: { minHeight: number; maxWidth: number; paddingVertical: number; paddingRight: number };
    desktopCardsHeight: number;
    desktopCardsInsetRight: number;
    desktopCardsSpread: number;
    desktopCardsWidth: number;
    desktopContainerDynamicStyle: { minHeight: number };
    hideCards: boolean;
    isLargeUI: boolean;
    titleDesktopStyle: { fontSize: number; lineHeight: number; maxWidth: number };
  };
  animateDesktopPhotos: boolean;
  desktopImageSources: [ImageSourcePropType, ImageSourcePropType, ImageSourcePropType] | undefined;
}): React.ReactNode {
  return isLargeUI && !layout.hideCards && animateDesktopPhotos && desktopImageSources ? (
    <View style={[styles.desktopCardsLayer, { paddingRight: layout.desktopCardsInsetRight }]} pointerEvents="none">
      <AnimatedDesktopPhotos
        height={layout.desktopCardsHeight}
        sources={desktopImageSources}
        spread={layout.desktopCardsSpread}
        width={layout.desktopCardsWidth}
      />
    </View>
  ) : (
    <DesktopPromoCards
      height={layout.desktopCardsHeight}
      insetRight={layout.desktopCardsInsetRight}
      spread={layout.desktopCardsSpread}
      visible={isLargeUI ? !layout.hideCards : false}
      width={layout.desktopCardsWidth}
    />
  );
}
